import React, { useRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import RibbonItem from 'common/components/atoms/RibbonItem';
import OutsideAlerter from 'common/OutsideAlerter';
import HeaderContainer from 'home/AssetHierarchy/components/atoms/HeaderContainer';
import FlexContainer from '../../atoms/FlexContainer';

const RibbonsContainer = styled(FlexContainer)`
  height: 52px;
  align-items: center;
  width: max-content;
  justify-content: space-between;
  margin-top: auto;
  margin-bottom: auto;
  padding-left: 10px;
`;

const Dropdown = styled.div`
  display: block;
  position: absolute;
  margin-left: -40px;
  background-color: #f1f1f1;
  border: 0.5px lightgrey;
  border-radius: 4px;
  z-index: 5;
  box-shadow: rgba(60, 61, 58, 0.24) 0px 0px 8px 0px;
  border-radius: 10px;
`;

const ChartTab = ({
  tabItems,
  selectedTabItem,
  toggleShowTabOptions
}) => {
  const tabStripRef = useRef(null);

  // OutsideAlerter wraps only the dropdown, so clicking a DIFFERENT tab counts as an
  // outside click. Closing here as well would cancel that tab's own toggle and the tab
  // would need a second click to open, so defer to it and only close on a true outside
  // click. OutsideAlerter passes the DOM event; never feed it to toggleShowTabOptions,
  // which expects a tab id.
  const handleOutsideClick = (event) => {
    const target = event && event.target;
    if (target && tabStripRef.current && tabStripRef.current.contains(target)) return;
    toggleShowTabOptions('');
  };

  return (
    <HeaderContainer height="35px" border direction="column">
      <RibbonsContainer innerRef={tabStripRef}>
        {tabItems.map((item, idx) => {
          if (item.hidden) return null;
          return (
            <FlexContainer
              key={idx}
              onClickCapture={() => { item.onClick(); }}
              direction="column"
              justifyContent="space-between"
              alignItems="center"
            >
              {/* Every caller localizes `text` itself; `value` is the locale-independent
                  identity used for the open/closed dropdown check below. */}
              <RibbonItem flexDirection="column" fontWeight="400">
                {item.text}
              </RibbonItem>
              {selectedTabItem === (item.value || item.text) && item.dropdown && (
                <OutsideAlerter open handleClick={handleOutsideClick} event="click">
                  <Dropdown key={item.id}>
                    {item.dropdown.map((i, index) => {
                      if (i.hidden) return null;
                      return (
                        <RibbonItem
                          selected={i.selected}
                          disabled={i.disabled}
                          padding="0.8em 1em"
                          onClick={i.onClick}
                          key={`sidedropdown-${item.id}-idx-${index}`}
                        >
                          <span title={i.title}>{i.text}</span>
                        </RibbonItem>
                      );
                    })}
                  </Dropdown>
                </OutsideAlerter>
              )}
            </FlexContainer>
          );
        })}
      </RibbonsContainer>
    </HeaderContainer>
  );
};

ChartTab.propTypes = {
  tabItems: PropTypes.arrayOf(PropTypes.shape({
    // Stable, locale-independent identity used to match `selectedTabItem`. `text` is
    // always the caller-localized label and must never be used as an identity, or the
    // open/closed check breaks the moment the label is translated.
    value: PropTypes.string,
    text: PropTypes.string,
    hidden: PropTypes.bool,
    dropdown: PropTypes.array,
    onClick: PropTypes.func
  })),
  selectedTabItem: PropTypes.string,
  toggleShowTabOptions: PropTypes.func
};

ChartTab.defaultProps = {
  tabItems: [],
  selectedTabItem: '',
  toggleShowTabOptions: () => {}
};

export default withTranslation()(ChartTab);
